import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { fetchAllRecords, fetchRecordCount, fetchRecords } from "../api/client";
import { DownloadIcon, SearchIcon } from "../components/Icons";
import { RecordTimeline } from "../components/RecordTimeline";
import { StatusToast } from "../components/StatusToast";
import { isLocalDateKey } from "../lib/records";
import { recordVisuals } from "../lib/recordVisuals";
import type { HealthRecord, RecordKind } from "../types";
import { PageError, PageLoading } from "./DashboardPage";

const ReportExportModal = lazy(() => import("../components/ReportExportModal").then((module) => ({ default: module.ReportExportModal })));
const RecordModal = lazy(() => import("../components/RecordModal").then((module) => ({ default: module.RecordModal })));
const DatePickerField = lazy(() => import("../components/DatePickerField").then((module) => ({ default: module.DatePickerField })));

const pageSize = 50;
const filters: Array<{ value: RecordKind | undefined; labelKey: string }> = [
  { value: undefined, labelKey: "history.filters.all" },
  { value: "weight", labelKey: "recordKinds.weight" },
  { value: "vitals", labelKey: "recordKinds.vitals" },
  { value: "exercise", labelKey: "recordKinds.exercise" },
  { value: "appointment", labelKey: "recordKinds.appointment" },
];

interface HistoryPageData {
  records: HealthRecord[];
  hasMore: boolean;
  nextOffset: number;
}

export function HistoryPage() {
  const { t } = useTranslation();
  const [reportOpen, setReportOpen] = useState(false);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HealthRecord>();
  const [statusMessage, setStatusMessage] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKind = searchParams.get("kind");
  const kind = isRecordKind(requestedKind) ? requestedKind : undefined;
  const requestedFrom = searchParams.get("from");
  const requestedTo = searchParams.get("to");
  const from = isLocalDateKey(requestedFrom) ? requestedFrom : undefined;
  const to = isLocalDateKey(requestedTo) ? requestedTo : undefined;
  const search = searchParams.get("q")?.trim() ?? "";
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const hasFilters = Boolean(kind || from || to || search);

  const records = useInfiniteQuery({
    queryKey: ["records", "history", kind ?? "all", from ?? "any", to ?? "any", search, timezone],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<HistoryPageData> => {
      const result = await fetchRecords({ kind, from, to, search, timezone, limit: pageSize + 1, offset: pageParam });
      return { records: result.slice(0, pageSize), hasMore: result.length > pageSize, nextOffset: pageParam + pageSize };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextOffset : undefined,
    placeholderData: (previous) => previous,
  });
  const exportRecords = useQuery({
    queryKey: ["records", "report", "all"],
    queryFn: () => fetchAllRecords(),
    enabled: reportOpen,
    staleTime: 30_000,
  });
  const recordCount = useQuery({
    queryKey: ["records", "history-count", kind ?? "all", from ?? "any", to ?? "any", search, timezone],
    queryFn: () => fetchRecordCount({ kind, from, to, search, timezone }),
  });
  const visibleRecords = useMemo(() => records.data?.pages.flatMap((page) => page.records) ?? [], [records.data]);

  function updateParam(name: "kind" | "from" | "to" | "q", value: string | undefined) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(name, value);
    else next.delete(name);
    if (name === "from" && value && to && value > to) next.delete("to");
    if (name === "to" && value && from && value < from) next.delete("from");
    setSearchParams(next, { replace: true });
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("historySearch");
    updateParam("q", typeof value === "string" ? value.trim() || undefined : undefined);
  }

  function handleEmptyAction() {
    if (hasFilters) {
      setSearchParams({}, { replace: true });
      return;
    }
    setRecordModalOpen(true);
  }

  function openReport() {
    setReportOpen(true);
    if (exportRecords.isError) void exportRecords.refetch();
  }

  const dismissStatus = useCallback(() => setStatusMessage(undefined), []);

  if (records.isPending) return <PageLoading label={t("history.loading")} />;
  if (records.isError) return <PageError onRetry={() => void records.refetch()} />;

  return (
    <div className="mx-auto max-w-5xl">
      <header className="flex flex-col gap-5 border-b border-line pb-7 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="page-eyebrow">{t("history.eyebrow")}</p>
          <h1 className="page-title">{t("history.title")}</h1>
          <p className="page-description mt-3">{t("history.description")}</p>
        </div>
        <button type="button" disabled={reportOpen && exportRecords.isPending} onClick={openReport} className="pressable inline-flex min-h-11 items-center justify-center gap-2 self-start rounded-xl border border-line-strong bg-surface px-4 py-2.5 text-sm font-bold text-ink shadow-sm hover:bg-surface-raised focus-ring disabled:cursor-wait disabled:opacity-60 sm:self-auto">
          <DownloadIcon className="size-4" />
          {reportOpen && exportRecords.isPending ? t("history.preparingExport") : t("actions.exportPdf")}
        </button>
      </header>

      <section aria-label={t("history.filterLabel")} className="surface-elevated my-6 rounded-[1.2rem] p-4 sm:p-5">
        <form key={search} onSubmit={submitSearch} className="flex flex-col gap-2 sm:flex-row">
          <label className="relative min-w-0 flex-1">
            <span className="sr-only">{t("history.searchLabel")}</span>
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
            <input type="search" name="historySearch" defaultValue={search} maxLength={200} placeholder={t("history.searchPlaceholder")} className="field-input min-h-11 pl-10" />
          </label>
          <button type="submit" className="pressable min-h-11 rounded-xl bg-ink px-5 py-2 text-sm font-bold text-canvas hover:opacity-90 focus-ring">{t("history.searchAction")}</button>
        </form>

        <Suspense fallback={<div className="mt-4 grid grid-cols-2 gap-3" aria-hidden="true"><div className="skeleton-block h-16" /><div className="skeleton-block h-16" /></div>}>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <DatePickerField label={t("history.fromDate")} value={from ?? ""} onChange={(value) => updateParam("from", value || undefined)} clearable />
            <DatePickerField label={t("history.toDate")} value={to ?? ""} onChange={(value) => updateParam("to", value || undefined)} clearable align="end" />
          </div>
        </Suspense>

        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={t("history.kindFilterLabel")}>
          {filters.map((filter) => (
            <button
              key={filter.labelKey}
              type="button"
              aria-pressed={filter.value === kind}
              onClick={() => updateParam("kind", filter.value)}
              className={`pressable inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold focus-ring ${filter.value === kind ? filter.value ? recordVisuals[filter.value].activeClass : "border-ink bg-ink text-canvas" : "border-line bg-surface text-muted hover:border-line-strong hover:text-ink"}`}
            >
              {filter.value ? <span aria-hidden="true" className={`size-2 rounded-full ${recordVisuals[filter.value].dotClass}`} /> : null}
              {t(filter.labelKey)}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
          <p className="text-xs font-semibold text-muted tabular-nums" aria-live="polite">
            {records.hasNextPage && recordCount.data !== undefined
              ? t("history.loadedOfTotal", { loaded: visibleRecords.length, total: recordCount.data })
              : t(records.hasNextPage ? "history.loadedRecords" : "history.visibleRecords", { count: visibleRecords.length })}
          </p>
          {hasFilters ? <button type="button" onClick={() => setSearchParams({}, { replace: true })} className="pressable min-h-11 rounded-lg px-3 py-2 text-xs font-bold text-teal-700 hover:bg-teal-50 focus-ring">{t("history.clearFilters")}</button> : null}
        </div>
      </section>

      {exportRecords.isError ? <p role="alert" className="mb-5 rounded-xl bg-pulse-soft px-4 py-3 text-sm font-semibold text-pulse-dark">{t("history.exportLoadFailed")}</p> : null}

      <RecordTimeline records={visibleRecords} emptyTitle={t("history.emptyTitle")} emptyDescription={t("history.emptyDescription")} onEditRecord={setEditingRecord} emptyActionLabel={t(hasFilters ? "history.clearFilters" : "actions.logRecord")} onEmptyAction={handleEmptyAction} />

      {records.hasNextPage ? (
        <div className="mt-6 text-center">
          <button type="button" disabled={records.isFetchingNextPage} onClick={() => void records.fetchNextPage()} className="pressable min-h-11 rounded-xl border border-line-strong bg-surface px-5 py-2.5 text-sm font-bold text-ink hover:bg-surface-raised focus-ring disabled:cursor-wait disabled:opacity-60">
            {records.isFetchingNextPage ? t("history.loadingOlder") : t("history.loadOlder")}
          </button>
        </div>
      ) : null}
      {records.isFetchNextPageError ? <p role="alert" className="mt-4 rounded-xl bg-pulse-soft px-4 py-3 text-sm font-semibold text-pulse-dark">{t("history.loadOlderFailed")}</p> : null}

      {reportOpen && exportRecords.data ? <Suspense fallback={null}><ReportExportModal onClose={() => setReportOpen(false)} records={exportRecords.data} initialKind={kind} initialFrom={from} initialTo={to} /></Suspense> : null}
      {editingRecord ? <Suspense fallback={null}><RecordModal open record={editingRecord} onClose={() => setEditingRecord(undefined)} onSaved={() => { setEditingRecord(undefined); setStatusMessage(t("recordForm.updated")); }} /></Suspense> : null}
      {recordModalOpen ? <Suspense fallback={null}><RecordModal open onClose={() => setRecordModalOpen(false)} onSaved={() => { setRecordModalOpen(false); setStatusMessage(t("recordForm.saved")); }} /></Suspense> : null}
      <StatusToast message={statusMessage} onDismiss={dismissStatus} />
    </div>
  );
}

function isRecordKind(value: string | null): value is RecordKind {
  return value === "weight" || value === "vitals" || value === "exercise" || value === "appointment";
}
